import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  accesRoles,
  apiInstance,
  Dispatch,
  EXPORT_PAYMENTS,
  GET_ELECTRONIC_DOCS,
  PROVISION_ELECTRONIC_DOCS,
  RootState,
} from "shared";
import { getHealthProviders } from "shared/api/services";
import { HealthProviderResponse } from "pages/autorization-payments/types";
import {
  downloadDocument,
  downloadsTitle,
} from "shared/utils";
import { columnsTableAutorizations } from "../helper/columnsTableAutorizations";
import { MenuActions } from "../components/MenuActions";
import { fieldsFormAutorizations } from "../helper/fieldsFormAutorizations";
import { convertPaymentAuthorization } from "shared/mappers";
import dayjs from "dayjs";
import { useCheckRoleAccess } from "shared/hooks/useCheckRoleAccess";
import { parallelData, singleData } from "shared/api/services";
import Swal from "sweetalert2";
import { ButtonAction } from "shared/components";
import { Link } from "react-router-dom";
import {
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const PROVISIONED_SUCCESS = "DOCUMENT_PROVISIONED";

const PROVISION_ERROR_MESSAGES: Record<string, string> = {
  DOCUMENT_NOT_PROVISIONED: "Documento no provisionado",
  DOCUMENT_ALREADY_PROVISIONED: "El documento ya fue provisionado",
};

export default function useAutorizationPayments() {
  const dispatch = useDispatch<Dispatch>();

  const { content } = useSelector(
    (state: RootState) => state.electronicDocuments,
  );
  const optionsDropdown = useSelector(
    (state: RootState) => state.optionsDropdowns,
  );
  const summationRows = useSelector((state: RootState) => state.summationRows);
  const [hasContract, setHasContract] = useState(false);
  const [columns, setColumns] = useState<any[]>([]);
  const [rowSelected, setRowSelected] = useState<number[]>([]);
  const [rowContentSelected, setRowContentSelected] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [dataFields, setDataFields] = useState<any[]>([]);
  const { checkRoles } = useCheckRoleAccess();
  const valuesResultsPages = [50, 100, 250, 500];
  const [showBtnGenerateCertificate, setShowBtnGenerateCertificate] =
    useState(false);
  const [showBtnInitiatePayment, setShowBtnInitiatePayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [providers, setProviders] = useState<HealthProviderResponse | null>(null);
  const [providersPage, setProvidersPage] = useState(0);
  const [providersSize, setProvidersSize] = useState(50);
  const [providerInvoices, setProviderInvoices] = useState<Record<string, any>>({});
  const [providerInvoicesPage, setProviderInvoicesPage] = useState<Record<string, number>>({});
  const [providerInvoicesSize, setProviderInvoicesSize] = useState<Record<string, number>>({});
  const [providerInvoicesLoading, setProviderInvoicesLoading] = useState<Record<string, boolean>>({});
  const [appliedFilters, setAppliedFilters] = useState<any>({});

  useEffect(() => {
    const columnsTransform: any[] = [
      ...columnsTableAutorizations(!hasContract),
      {
        field: "actions",
        headerName: "",
        minWidth: 120,
        renderCell(params: any) {
          return (
            <MenuActions>
              <MenuItem>
                <ButtonAction roles={accesRoles.auditor} disabled={false}>
                  <Link
                    to={`/autorizaciones/detalle_documento/${params?.id}`}
                    data-testid="link-detailed"
                  >
                    Ver factura
                  </Link>
                </ButtonAction>
              </MenuItem>
            </MenuActions>
          );
        },
      },
    ];
    setColumns(columnsTransform);
  }, [content]);

  useEffect(() => {
    const fields = fieldsFormAutorizations(optionsDropdown);
    setDataFields(fields);
  }, [optionsDropdown]);

  useEffect(() => {
    optionsDropdown.users.length === 0 && dispatch.optionsDropdowns.getUsers();
    optionsDropdown.paymentsLines.length === 0 &&
      dispatch.optionsDropdowns.getPaymentsLines();
    optionsDropdown.paymentsStates.length === 0 &&
      dispatch.optionsDropdowns.getPaymentStates();

    dispatch.invoiceStates.getInvoiceStatesAutorizations();
  }, []);

  useEffect(() => {
    getHealthProviders({
      page: providersPage,
      size: providersSize,
      isContract: hasContract,
      ...appliedFilters,
    })
      .then((response) => {
        setProviders(response);
      })
      .catch((error) => {
        console.error("Error loading providers:", error);
        setProviders(null);
      });
  }, [providersPage, providersSize, hasContract, appliedFilters]);

  useEffect(() => {
    // Reset cached invoices when filters change to ensure fresh queries per provider
    setProviderInvoices({});
    setProviderInvoicesPage({});
    setProviderInvoicesSize({});
    setProviderInvoicesLoading({});
  }, [appliedFilters, hasContract]);

  const fillSummationRows = (newSelected: number[]) => {
    // Get all invoices from all providers
    const allInvoices = Object.values(providerInvoices).flatMap(
      (providerData: any) => providerData?.content || []
    );

    const matchingElements = allInvoices?.filter((item: any) =>
      newSelected.includes(item.id),
    );

    const totalProviders = matchingElements?.filter(
      (item) => item.authorizedBy,
    ).length;
    const sumTotalValue = matchingElements?.reduce(
      (total, item: any) => total + item.valueEndorsed,
      0,
    );

    const newValuesSummation = [
      {
        name: "Recuento",
        value: matchingElements?.length.toString() ?? "0",
      },
      {
        name: "Total facturas seleccionadas",
        value: totalProviders?.toString() ?? "0",
      },
      {
        name: "Total",
        value: sumTotalValue?.toLocaleString() ?? "0",
      },
    ];
    dispatch.summationRows.setSummationRows(newValuesSummation);
  };

  const handleChangeCheck = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: any,
    row: any,
  ) => {
    if (event.target.checked) {
      const currentNits = new Set(rowContentSelected.map((r) => r.nit));
      if (currentNits.size > 0 && !currentNits.has(row.nit)) {
        setRowContentSelected([row]);
        setRowSelected([id]);
        validateSelection([row]);
        validateInitiatePayment([row]);
        fillSummationRows([id]);
        return;
      }
    }

    const updatedRowContentSelected = event.target.checked
      ? [...rowContentSelected, row]
      : rowContentSelected.filter((item) => item.id !== id);

    setRowContentSelected(updatedRowContentSelected);
    validateSelection(updatedRowContentSelected);
    validateInitiatePayment(updatedRowContentSelected);

    const selectedIndex = rowSelected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(rowSelected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(rowSelected.slice(1));
    } else if (selectedIndex === rowSelected.length - 1) {
      newSelected = newSelected.concat(rowSelected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        rowSelected.slice(0, selectedIndex),
        rowSelected.slice(selectedIndex + 1),
      );
    }
    setRowSelected(newSelected);
    fillSummationRows(newSelected);
  };

  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    rows: any,
  ) => {
    if (event.target.checked) {
      const uniqueNits = new Set(rows.map((r: any) => r.nit));
      if (uniqueNits.size > 1) {
        const firstNit = rows[0].nit;
        const filteredRows = rows.filter((r: any) => r.nit === firstNit);
        const newSelected = filteredRows?.map((x: any) => x.id);
        setRowContentSelected(filteredRows);
        setRowSelected(newSelected || []);
        validateSelection(filteredRows);
        validateInitiatePayment(filteredRows);
        fillSummationRows(newSelected!);
        return;
      }
    }

    const updatedRowContentSelected = event.target.checked ? rows : [];
    setRowContentSelected(updatedRowContentSelected);
    validateSelection(updatedRowContentSelected);
    validateInitiatePayment(updatedRowContentSelected);
    if (event.target.checked) {
      const newSelected = rows?.map((x: any) => x.id);
      setRowSelected(newSelected || []);
      fillSummationRows(newSelected!);
      return;
    }
    setRowSelected([]);
    fillSummationRows([]);
  };

  const handleChangeContract = (value: boolean) => {
    setRowSelected([]);
    fillSummationRows([]);
    setRowContentSelected([]);
    setShowBtnGenerateCertificate(false);
    setShowBtnInitiatePayment(false);
    setHasContract(value);
    setProvidersPage(0);
    // Reload providers when contract changes
    getHealthProviders({
      page: 0,
      size: providersSize,
      isContract: value,
      ...appliedFilters,
    })
      .then((response) => {
        setProviders(response);
      })
      .catch((error) => {
        console.error("Error loading providers:", error);
        setProviders(null);
      });
  };

  const getPayment = async (url: string, params: {}) => {
    // No longer loads invoices, now loads providers
    await getHealthProviders({
      page: providersPage,
      size: providersSize,
      isContract: hasContract,
      ...appliedFilters,
    })
      .then((response) => {
        setProviders(response);
      })
      .catch((error) => {
        console.error("Error loading providers:", error);
        setProviders(null);
      });
  };

  const getValueDatePickerStart = (datePickerValue: any): Date | null => {
    if (!datePickerValue) return null;
    if (Array.isArray(datePickerValue)) {
      return datePickerValue[0] ? new Date(datePickerValue[0]) : null;
    }
    return new Date(datePickerValue);
  };

  const getValueDatePickerEnd = (datePickerValue: any): Date | null => {
    if (!datePickerValue) return null;
    if (Array.isArray(datePickerValue)) {
      return datePickerValue[1] ? new Date(datePickerValue[1]) : null;
    }
    return new Date(datePickerValue);
  };

  const transformFiltersToParams = (filters: any) => {
    // Check if filters are already formatted (from buildUrl) or need transformation (from form)
    const isFormatted = filters.attentionPeriodStartedAt !== undefined;

    if (isFormatted) {
      // Filters are already formatted, use them directly
      return {
        userId: filters.userId || "",
        paymentLineId: filters.paymentLineId || "",
        stateInvoice: filters.stateInvoice,
        attentionPeriodStartedAt: filters.attentionPeriodStartedAt || "",
        attentionPeriodEndedAt: filters.attentionPeriodEndedAt || "",
        documentNumber: filters.documentNumber,
        employerIdNumber: filters.employerIdNumber,
        filingNumber: filters.filingNumber,
        filingNumberCreatedAtStart: filters.filingNumberCreatedAtStart || "",
        filingNumberCreatedAtEnd: filters.filingNumberCreatedAtEnd || "",
        isCertified: "false",
        states: filters.states || "",
        isContract: hasContract,
        orderBy: "1",
      };
    }

    // Filters need transformation (from form)
    const startInvoiceDateServiceAt = getValueDatePickerStart(filters.invoiceDateService);
    const endInvoiceDateServiceAt = getValueDatePickerEnd(filters.invoiceDateService);
    const startDateRadicadoAt = getValueDatePickerStart(filters.radicadoAt);
    const endDateRadicadoAt = getValueDatePickerEnd(filters.radicadoAt);
    const userId = filters.authorizationValue?.id || "";

    return {
      userId: userId,
      paymentLineId: filters.paymentLine,
      stateInvoice: filters.stateInvoice,
      attentionPeriodStartedAt: startInvoiceDateServiceAt
        ? dayjs.utc(startInvoiceDateServiceAt).format("YYYY-MM-DD")
        : "",
      attentionPeriodEndedAt: endInvoiceDateServiceAt
        ? dayjs.utc(endInvoiceDateServiceAt).format("YYYY-MM-DD")
        : "",
      documentNumber: filters.invoiceReference,
      employerIdNumber: filters.nitEntidad,
      filingNumber: filters.radicado,
      filingNumberCreatedAtStart: startDateRadicadoAt
        ? dayjs.utc(startDateRadicadoAt).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        : "",
      filingNumberCreatedAtEnd: endDateRadicadoAt
        ? dayjs.utc(endDateRadicadoAt).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        : "",
      isCertified: "false",
      states: filters.states || "",
      isContract: hasContract,
      orderBy: "1",
    };
  };

  const getProviderInvoices = async (
    providerCode: string,
    filters: any = {},
    page: number = 0,
    size: number = 50
  ) => {
    setProviderInvoicesLoading((prev) => ({ ...prev, [providerCode]: true }));
    
    const transformedFilters = transformFiltersToParams(filters);
    const params = {
      ...transformedFilters,
      providerId: providerCode,
      page,
      size,
    };

    await apiInstance
      .get(`${GET_ELECTRONIC_DOCS}/page_payments`, {
        params,
      })
      .then((response) => {
        const data = convertPaymentAuthorization(response?.data);
        setProviderInvoices((prev) => ({
          ...prev,
          [providerCode]: data,
        }));
        setProviderInvoicesPage((prev) => ({
          ...prev,
          [providerCode]: data.number,
        }));
        setProviderInvoicesSize((prev) => ({
          ...prev,
          [providerCode]: data.size,
        }));
      })
      .catch((error) => {
        console.error("Error loading provider invoices:", error);
        setProviderInvoices((prev) => ({
          ...prev,
          [providerCode]: null,
        }));
      })
      .finally(() => {
        setProviderInvoicesLoading((prev) => ({
          ...prev,
          [providerCode]: false,
        }));
      });
  };

  const handleSetProviderInvoicesPage = (providerCode: string, page: number) => {
    setProviderInvoicesPage((prev) => ({
      ...prev,
      [providerCode]: page,
    }));
  };

  const handleSetProviderInvoicesSize = (providerCode: string, size: number) => {
    setProviderInvoicesSize((prev) => ({
      ...prev,
      [providerCode]: size,
    }));
  };

  const validateSelection = (selectedRows: any[]) => {
    if (selectedRows.length === 0) {
      setShowBtnGenerateCertificate(false);
      setShowBtnInitiatePayment(false);
      return;
    }

    const isValidSelection =
      selectedRows.every((row) => !row.withCertificate) &&
      selectedRows.every(
        (row) =>
          dayjs.utc(row.endDateofService).year() ===
          dayjs.utc(row.initialServiceDate).year(),
      ) &&
      checkRoles(accesRoles.auditor) &&
      new Set(selectedRows.map((row) => row.paymentLine)).size === 1 &&
      new Set(selectedRows.map((row) => row.nit)).size === 1;
    setShowBtnGenerateCertificate(isValidSelection);
  };

  const validateInitiatePayment = (selectedRows: any[]) => {
    if (selectedRows.length === 0) {
      setShowBtnInitiatePayment(false);
      return;
    }

    const isValidInitiatePayment =
      !hasContract &&
      selectedRows.length >= 1 &&
      new Set(selectedRows.map((row) => row.nit)).size === 1;
    setShowBtnInitiatePayment(isValidInitiatePayment);
  };

  const onAprovisionar = async () => {
    if (rowContentSelected.length > 20) {
      Swal.fire({
        title: "Límite excedido",
        text: "Solo se pueden provisionar máximo 20 documentos a la vez.",
        icon: "error",
        timer: 4000,
      });
      return;
    }

    const documents = rowContentSelected.map((row: any) => row.invoiceNumber);
    setIsLoading(true);

    await apiInstance
      .put(PROVISION_ELECTRONIC_DOCS, { documents })
      .then((response) => {
        const results: { documentNumber: string; details: string }[] =
          response.data;
        const successCount = results.filter(
          (r) => r.details === PROVISIONED_SUCCESS,
        ).length;

        dispatch.messageModal.setMessageModal({
          show: true,
          title: `Resultado del provisionamiento (${successCount}/${results.length} exitosos)`,
          size: "medium",
          OkTitle: "Cerrar",
          description: (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Documento</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Estado</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Detalle</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r) => {
                  const ok = r.details === PROVISIONED_SUCCESS;
                  return (
                    <TableRow
                      key={r.documentNumber}
                      sx={{ backgroundColor: ok ? "#f0fff4" : "#fff5f5" }}
                    >
                      <TableCell>{r.documentNumber}</TableCell>
                      <TableCell>
                        {ok ? (
                          <CheckCircleIcon
                            sx={{ color: "#2e7d32", fontSize: 18 }}
                          />
                        ) : (
                          <CancelIcon sx={{ color: "#c62828", fontSize: 18 }} />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: ok ? "#2e7d32" : "#c62828",
                          fontSize: "0.8rem",
                        }}
                      >
                        {ok
                          ? "Documento provisionado exitosamente"
                          : (PROVISION_ERROR_MESSAGES[
                              r.details.replace(
                                "DocumentProvisionErrorCode.",
                                "",
                              )
                            ] ??
                            r.details.replace(
                              "DocumentProvisionErrorCode.",
                              "",
                            ))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ),
        });
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const onGenerateCertificate = () => {
    setIsLoading(true);
    const requests = {
      respExcel: singleData(
        `${GET_ELECTRONIC_DOCS}/${EXPORT_PAYMENTS}?states=&ids=${rowSelected.join(",")}`,
        {
          responseType: "blob",
          timeout: 0,
          headers: {
            "X-Timezone-Id": "America/Bogota",
            "Accept-Language": "es-CO",
          },
        },
      ),
      respPdf: singleData(
        `${GET_ELECTRONIC_DOCS}/${EXPORT_PAYMENTS}_pdf?ids=${rowSelected.join(",")}`,
        {
          responseType: "blob",
        },
      ),
    };

    parallelData(requests)
      .then((responseMap) => {
        setTimeout(() => {
          responseMap.respExcel &&
            downloadDocument(
              responseMap.respExcel,
              downloadsTitle.successDocument,
              "Documento descargado con éxito",
              "success",
            );
        }, 1000);
        setTimeout(() => {
          responseMap.respPdf &&
            downloadDocument(
              responseMap.respPdf,
              downloadsTitle.successDocument,
              "Documento descargado con éxito",
              "success",
            );
        }, 1000);
      })
      .catch((e) => {
        Swal.fire({
          title: "No se encontró información de la factura",
          icon: "error",
          timer: 3000,
        });
      })
      .finally(() => setIsLoading(false));
  };

  return {
    columns,
    rowSelected,
    rowContentSelected,
    handleChangeCheck,
    handleSelectAllClick,
    handleChangeContract,
    onGenerateCertificate,
    showForm,
    setShowForm,
    dataFields,
    summationRows,
    valuesResultsPages,
    hasContract,
    showBtnGenerateCertificate,
    showBtnInitiatePayment,
    isLoading,
    getPayment,
    onAprovisionar,
    providers,
    providersPage,
    providersSize,
    setProvidersPage,
    setProvidersSize,
    providerInvoices,
    providerInvoicesPage,
    providerInvoicesSize,
    providerInvoicesLoading,
    getProviderInvoices,
    setProviderInvoicesPage: handleSetProviderInvoicesPage,
    setProviderInvoicesSize: handleSetProviderInvoicesSize,
    appliedFilters,
    setAppliedFilters,
  };
}
